import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { useAuth, loginWithGoogle } from "../lib/auth";
import { Button } from "./ui/button";
import { Plane, LogOut, Menu, X, ChevronDown } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const closeMenu = () => setOpen(false);

  const isActive = (path) =>
    loc.pathname === path || loc.pathname.startsWith(path + "/");

  const linkClass = (path) =>
    isActive(path)
      ? "px-3 py-2 rounded-sm text-[13px] font-medium text-[var(--brand-primary)] bg-[var(--brand-light)] transition-colors"
      : "px-3 py-2 rounded-sm text-[13px] font-medium text-slate-600 hover:text-[var(--brand-primary)] hover:bg-slate-100 transition-colors";

  const sheetLinkClass = (path) =>
    isActive(path)
      ? "block py-3 px-2 text-[var(--brand-primary)] bg-[var(--brand-light)] rounded-sm text-sm font-semibold"
      : "block py-3 px-2 text-slate-700 hover:text-[var(--brand-primary)] text-sm font-medium border-b border-slate-100";

  const NavLinks = ({ inSheet = false }) => {
    const cls = (p) => (inSheet ? sheetLinkClass(p) : linkClass(p));
    return (
      <>
        <Link to="/dashboard" className={cls("/dashboard")} onClick={closeMenu} data-testid="nav-dashboard">Dashboard</Link>
        {["student"].includes(user.role) && (
          <Link to="/debriefs" className={cls("/debriefs")} onClick={closeMenu} data-testid="nav-debriefs">Debriefs</Link>
        )}
        {["instructor", "school_admin", "super_admin"].includes(user.role) && (
          <Link to="/instructor" className={cls("/instructor")} onClick={closeMenu} data-testid="nav-instructor">Instructor</Link>
        )}
        <Link to="/pricing" className={cls("/pricing")} onClick={closeMenu} data-testid="nav-pricing">Plans</Link>
        {(user.role === "school_admin" || user.role === "super_admin") && (
          <Link to="/admin" className={cls("/admin")} onClick={closeMenu} data-testid="nav-admin">Admin</Link>
        )}
      </>
    );
  };

  return (
    <nav className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200" data-testid="main-nav">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
        {/* Logo */}
        <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-3 group flex-shrink-0" data-testid="logo-link">
          <div className="w-9 h-9 rounded-sm bg-[var(--brand-primary)] flex items-center justify-center">
            <Plane className="w-[18px] h-[18px] text-white" strokeWidth={2} />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-[15px] font-bold text-slate-900 tracking-tight">Aviatron</span>
            <span className="text-[10px] font-medium tracking-[0.14em] uppercase text-slate-500">Flight Training</span>
          </div>
        </Link>

        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-1">
          {!user && (
            <>
              <Link to="/pricing" className={linkClass("/pricing")} data-testid="nav-pricing">Pricing</Link>
              <Button
                className="bg-[var(--brand-primary)] hover:opacity-90 text-[var(--brand-primary-fg)] rounded-sm font-medium ml-2"
                onClick={loginWithGoogle}
                data-testid="nav-login-btn"
              >
                Sign in
              </Button>
            </>
          )}
          {user && (
            <>
              <NavLinks />
              <div className="ml-3 flex items-center gap-3 pl-3 border-l border-slate-200 relative">
                <button
                  className="flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-slate-100 transition-colors"
                  onClick={() => setProfileOpen((v) => !v)}
                  data-testid="nav-profile-toggle"
                >
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-7 h-7 rounded-full border border-slate-200" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-[var(--brand-light)] flex items-center justify-center text-[var(--brand-primary)] text-xs font-bold">
                      {(user.name || user.email || "?")[0]?.toUpperCase()}
                    </div>
                  )}
                  <div className="text-left">
                    <div className="text-[13px] font-medium text-slate-900 leading-tight" data-testid="nav-user-name">{user.name?.split(" ")[0] || user.email}</div>
                    <div className="text-[10px] text-slate-500 tracking-wider uppercase leading-tight">{user.role?.replace("_", " ")}</div>
                  </div>
                  <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                </button>
                {profileOpen && (
                  <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-slate-200 rounded-sm shadow-lg py-1.5" onMouseLeave={() => setProfileOpen(false)}>
                    <div className="px-3 py-2 border-b border-slate-100">
                      <div className="text-sm font-medium text-slate-900 truncate">{user.name || user.email}</div>
                      <div className="text-xs text-slate-500 truncate">{user.email}</div>
                    </div>
                    <button
                      onClick={logout}
                      className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                      data-testid="nav-logout-btn"
                    >
                      <LogOut className="w-3.5 h-3.5" /> Sign out
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Mobile hamburger */}
        <div className="lg:hidden flex items-center gap-3">
          {!user && (
            <Button
              className="bg-[var(--brand-primary)] hover:opacity-90 text-[var(--brand-primary-fg)] rounded-sm h-9 px-4 font-medium"
              onClick={loginWithGoogle}
              data-testid="nav-login-btn-mobile"
            >
              Sign in
            </Button>
          )}
          <button
            onClick={() => setOpen((v) => !v)}
            className="text-slate-700 hover:text-[var(--brand-primary)] p-2 border border-slate-200 rounded-sm"
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown sheet */}
      {open && user && (
        <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="nav-mobile-sheet">
          <div className="max-w-7xl mx-auto px-4 py-2">
            <div className="py-3 border-b border-slate-100 flex items-center gap-3">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-9 h-9 rounded-full border border-slate-200" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-[var(--brand-light)] flex items-center justify-center text-[var(--brand-primary)] font-bold">
                  {(user.name || user.email || "?")[0]?.toUpperCase()}
                </div>
              )}
              <div>
                <div className="text-sm font-medium text-slate-900">{user.name || user.email}</div>
                <div className="text-[10px] text-slate-500 tracking-wider uppercase">{user.role?.replace("_", " ")}</div>
              </div>
            </div>
            <NavLinks inSheet />
            <button
              onClick={() => { closeMenu(); logout(); }}
              className="flex items-center gap-2 w-full py-3 px-2 text-slate-700 hover:text-[var(--brand-primary)] text-sm font-medium"
              data-testid="nav-logout-btn-mobile"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
